import { Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

export interface SaveErrorInfo {
	statusCode: number | null;
	message: string;
	occurredAt: number;
}

/** Once the overlay has been shown (and dismissed) for a given failure streak,
 * wait this long before forcing it back onto the screen for a fresh failure —
 * long enough to not be a nag, short enough that the user cannot forget. */
const REESCALATION_COOLDOWN_MS = 3 * 60 * 1000;

/**
 * Tracks the outcome of the editor's auto-save calls (config, scene/image
 * JSON, ...) so a failure can never go unnoticed the way it used to
 * (PRO-209): a 413/500/network failure would previously reset a flag or get
 * logged to a signal nothing rendered, leaving the user editing on top of
 * unsaved work with no indication anything was wrong.
 *
 * Editor-scoped (provided alongside `EditorDataService`/`EditorConfigService`
 * in `EditorComponent.providers`) so every save path in one editor session
 * reports into the same place.
 */
@Injectable()
export class SaveErrorService {
	readonly error = signal<SaveErrorInfo | null>(null);
	readonly overlayVisible = signal(false);
	readonly canRetry = signal(false);

	private retryFn: (() => void) | null = null;
	private lastOverlayShownAt = 0;

	/** Registered by the call site right before it initiates a save, so a
	 * retry re-runs the *current* save (rebuilt from live state) rather than
	 * replaying the stale request that originally failed. */
	setRetry(fn: () => void): void {
		this.retryFn = fn;
		this.canRetry.set(true);
	}

	reportError(err: unknown): void {
		this.error.set(toSaveErrorInfo(err));

		const now = Date.now();
		if (!this.overlayVisible() && now - this.lastOverlayShownAt > REESCALATION_COOLDOWN_MS) {
			this.overlayVisible.set(true);
			this.lastOverlayShownAt = now;
		}
	}

	/** Called after any save succeeds — clears the failure streak entirely. */
	clear(): void {
		this.error.set(null);
		this.overlayVisible.set(false);
		this.retryFn = null;
		this.canRetry.set(false);
	}

	/** User acknowledged the full-screen overlay; the error itself (and the
	 * persistent banner) stays until a save actually succeeds. */
	dismissOverlay(): void {
		this.overlayVisible.set(false);
	}

	/** User clicked the persistent banner to bring the full-screen warning back. */
	reopenOverlay(): void {
		this.overlayVisible.set(true);
	}

	retry(): void {
		this.retryFn?.();
	}
}

function toSaveErrorInfo(err: unknown): SaveErrorInfo {
	if (err instanceof HttpErrorResponse) {
		return { statusCode: err.status, message: messageForStatus(err.status), occurredAt: Date.now() };
	}
	return { statusCode: null, message: 'Changes could not be saved.', occurredAt: Date.now() };
}

function messageForStatus(status: number): string {
	if (status === 413) {
		return 'This project is too large to save in one go. Try removing or shrinking some media, or contact support if the problem persists.';
	}
	if (status === 0) {
		return 'Could not reach the server. Check your internet connection.';
	}
	if (status >= 500) {
		return 'The server ran into an error while saving. Please try again shortly.';
	}
	if (status === 403) {
		return 'You no longer have permission to save changes to this project.';
	}
	return 'Changes could not be saved.';
}
